import { walletKey } from '../../lib/walletKey.js';
import { getInstantiatedAdditionalConnectorsStores, subscribeAdditionalConnectorsStores } from './additionalConnectorsStore.js';
import { mapWallet } from './mapWallet.js';
import { SLUGS_TO_FILTER } from '../../constants.js';
import { WALLET_REGISTRY_BATCH_LIMIT } from './registrySnapshot.js';
const WALLET_REGISTRY_URL = 'https://layerswap.io/app/api/wallet-registry';
const ERROR_RETRY_DELAY_MS = 30000;
const resolvedEntries = new Map();
const failedAt = new Map();
const pending = new Set();
const listeners = new Set();
let version = 0;
let cachedIndex = null;
const emit = () => {
    version += 1;
    listeners.forEach(listener => listener());
};
subscribeAdditionalConnectorsStores(() => {
    cachedIndex = null;
    emit();
});
const getBrowsedEntries = () => {
    if (cachedIndex)
        return cachedIndex;
    const index = new Map();
    for (const store of getInstantiatedAdditionalConnectorsStores()) {
        for (const wallet of store.getSnapshot().browseConnectors) {
            for (const value of [wallet.name, wallet.id]) {
                const key = walletKey(value);
                if (key && !index.has(key))
                    index.set(key, wallet);
            }
        }
    }
    cachedIndex = index;
    return index;
};
const getReadyStore = () => getInstantiatedAdditionalConnectorsStores().find(store => store.getSnapshot().browseMetadata.status === 'ready');
const isFailureBackoff = (key) => Date.now() - (failedAt.get(key) ?? -Infinity) < ERROR_RETRY_DELAY_MS;
export const getRegistryEntryByName = (name) => {
    const key = walletKey(name);
    if (!key)
        return undefined;
    return resolvedEntries.get(key) ?? getBrowsedEntries().get(key);
};
const postRegistryBatch = async (names, projectId) => {
    const res = await fetch(WALLET_REGISTRY_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ names, projectId }),
    });
    if (!res.ok)
        throw new Error(`wallet-registry failed: ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data?.wallets))
        throw new Error('wallet-registry: invalid response');
    return data.wallets;
};
const resolveRegistryEntries = async (requested) => {
    for (const key of requested.keys())
        pending.add(key);
    try {
        await Promise.all(getInstantiatedAdditionalConnectorsStores().map(store => store.ensureBrowseLoaded()));
        const store = getReadyStore();
        if (!store)
            return;
        const batch = [...requested].filter(([key]) => !resolvedEntries.has(key) && !getBrowsedEntries().has(key)).slice(0, WALLET_REGISTRY_BATCH_LIMIT);
        if (!batch.length)
            return;
        const wallets = await postRegistryBatch(batch.map(([, name]) => name), store.projectId);
        const index = new Map(wallets.flatMap((raw) => {
            const mapped = mapWallet(raw, store.projectId);
            if (SLUGS_TO_FILTER.includes(mapped.id))
                return [];
            return [[walletKey(mapped.name), mapped], [walletKey(mapped.id), mapped]];
        }));
        let matched = false;
        for (const [key] of batch) {
            const match = index.get(key) ?? null;
            resolvedEntries.set(key, match);
            matched || (matched = !!match);
        }
        if (matched)
            emit();
    }
    catch {
        for (const key of requested.keys())
            failedAt.set(key, Date.now());
    }
    finally {
        for (const key of requested.keys())
            pending.delete(key);
    }
};
export const requestRegistryEntriesByName = (names) => {
    if (!getReadyStore())
        return;
    const batch = new Map();
    for (const name of names) {
        const key = walletKey(name);
        if (!key || batch.has(key) || resolvedEntries.has(key) || pending.has(key) || isFailureBackoff(key))
            continue;
        batch.set(key, name);
    }
    if (batch.size)
        void resolveRegistryEntries(batch);
};
export const subscribeRegistryEntryIndex = (listener) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};
export const getRegistryEntryIndexVersion = () => version;
